import { Box, Button, Grid, List, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  canEditSelector,
  isLeaderSelector,
} from "../../../store/applicantsSlice";
import makeStyles from "@mui/styles/makeStyles";
import { Warning as WarningIcon } from "../Components/Icon";

import {
  getSubmitStatus,
  submitProposal,
  getProposalData,
  createProposalPDF,
} from "../helpers/GifService/applicantService";
import { useLoading } from "../../../providers/loadingProvider";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { proposalEligibilityStatus } from "../Constants";
import {
  proposalSelector,
  colleaguesSelector,
  setProposal,
} from "../../../store/proposalSlice";
import SubmitGroup from "../Components/SubmitGroup";

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: 600,
    color: "#4B4B4B",
  },
  errorTitle: {
    fontWeight: 600,
    color: "#4B4B4B",
    marginLeft: 10,
  },
  text: {
    color: "#4B4B4B",
  },
  error: {
    fontWeight: 400,
    color: "#ED1C24",
  },
  submitButton: {
    "&:hover": {
      backgroundColor: theme.palette.gif.green.hover,
    },
    color: "#FFFFFF",
    backgroundColor: theme.palette.gif.green.color,
    width: "10%",
    borderRadius: 10,
  },
  nested: {
    paddingLeft: theme.spacing(4),
  },
  link: {
    marginLeft: 5,
  },
  paper: {
    position: "absolute",
    width: "80%",
    backgroundColor: theme.palette.background.paper,
    border: "1px solid #000",
    borderRadius: 10,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    top: `7%`,
    left: `10%`,
    maxHeight: "85%",
    overflowY: "auto",
  },
  closeIcon: {
    top: -30,
    left: -35,
    marginBottom: -50,
  },
}));

export default function Submit() {
  const classes = useStyles();
  const proposal = useSelector(proposalSelector);
  const isLeader = useSelector(isLeaderSelector);
  const applicants = useSelector(colleaguesSelector);
  const dispatch = useDispatch();
  const canEdit = useSelector(canEditSelector);
  const [submitValidation, setSubmitValidation] = useState(null);
  const [hasErrors, setHasErrors] = useState(false);
  const loading = useLoading();
  const [isSubmit, setIsSubmit] = useState(false);
  const [finishLoading, setFinishLoading] = useState(false);

  const submitClicked = async () => {
    const loadingid = loading.addLoading("Submiting Proposal");
    const proposalId = proposal.id;
    createProposalPDF({ proposalId });
    await submitProposal({ proposalId });
    getProposalData().then((proposal) => {
      dispatch(setProposal(proposal));
    });
    setIsSubmit(true);
    loading.removeLoading(loadingid);
  };

  const getStatus = useCallback(async () => {
    const proposalId = proposal.id;
    const loadingid = loading.addLoading("validating proposal");
    const submitStatus = await getSubmitStatus({ proposalId });
    setSubmitValidation(submitStatus);
    loading.removeLoading(loadingid);
  }, [loading, proposal.id]);

  useEffect(() => {
    if (submitValidation && applicants) {
      submitValidation.proposalData.validations.forEach(
        (v) => !v.isValid && setHasErrors(true)
      );
      submitValidation.applicantsData.forEach((applicant) =>
        applicant.validations.forEach((v) => !v.isValid && setHasErrors(true))
      );
      setFinishLoading(true);
    }
  }, [submitValidation, applicants]);

  useEffect(() => {
    if (!proposal) return;
    if (
      proposal.eligibilitystatus === proposalEligibilityStatus.approvedByGIF ||
      proposal.eligibilitystatus === proposalEligibilityStatus.approvedByRA ||
      proposal.eligibilitystatus ===
        proposalEligibilityStatus.submittedByApplicationLeader ||
      proposal.eligibilitystatus ===
        proposalEligibilityStatus.submittedByResearchers ||
      proposal.eligibilitystatus === proposalEligibilityStatus.notEligible
    ) {
      setIsSubmit(true);
      setFinishLoading(true);
    } else getStatus();
  }, [proposal, getStatus]);

  return (
    <>
      {finishLoading &&
        (isSubmit ? (
          <Box padding={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography className={classes.title}>Thank you!</Typography>
              </Grid>
              <Grid item xs={12}>
                <Grid container>
                  <CheckCircleIcon
                    style={{ color: "#00A14B", marginRight: 5 }}
                  />
                  <Typography className={classes.text}>
                    Your application has been submitted
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        ) : hasErrors ? (
          <>
            <Grid container>
              <WarningIcon />
              <Typography className={classes.errorTitle}>
                Missing information:
              </Typography>
            </Grid>
            <List>
              <SubmitGroup group={submitValidation.proposalData} />
              {submitValidation.applicantsData.map((group) => (
                <SubmitGroup group={group} />
              ))}
            </List>
          </>
        ) : isLeader ? (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography className={classes.title}>Submission</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography className={classes.text}>
                Your application is ready for submission.
              </Typography>
              <Typography className={classes.text}>
                Before clicking the *submit* button, we suggest that you will
                click the *view PDF*. Viewing it before submitting allows you to
                edit the different parts of the proposal.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Button
                disableElevation
                variant="contained"
                className={classes.submitButton}
                disabled={!canEdit}
                onClick={submitClicked}
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Grid container>
            <WarningIcon />
            <Typography className={classes.errorTitle}>
              Only The Leader Can Submit the Proposal
            </Typography>
          </Grid>
        ))}
    </>
  );
}
